import { useMutation, UseMutationResult, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import { AffiliateDetail, UpdateAffiliateDto } from '../domain/types';
import { affiliateManagementService } from '../infra/affiliateManagementService';

interface UpdateAffiliateParams {
  projectId: string;
  affiliateId: string;
  body: UpdateAffiliateDto;
}

interface UseUpdateAffiliateResult {
  updateAffiliate: (params: UpdateAffiliateParams) => void;
  updateAffiliateAsync: (params: UpdateAffiliateParams) => Promise<AffiliateDetail>;
  isLoading: boolean;
  isError: boolean;
  isSuccess: boolean;
  error: Error | null;
}

export const useUpdateAffiliate = (): UseUpdateAffiliateResult => {
  const queryClient = useQueryClient();

  const {
    mutate,
    mutateAsync,
    isPending,
    isError,
    isSuccess,
    error,
  }: UseMutationResult<AffiliateDetail, Error, UpdateAffiliateParams> = useMutation({
    mutationFn: async ({ projectId, affiliateId, body }: UpdateAffiliateParams) => {
      return affiliateManagementService.update(projectId, affiliateId, body);
    },
    onSuccess: (_data, variables) => {
      toast.success('Affiliate updated successfully');
      queryClient.invalidateQueries({
        queryKey: ['affiliate-management', 'list'],
      });
      queryClient.invalidateQueries({
        queryKey: ['affiliate-management', 'detail', variables.projectId, variables.affiliateId],
      });
      queryClient.invalidateQueries({
        queryKey: ['project-affiliates'],
      });
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to update affiliate');
    },
  });

  return {
    updateAffiliate: mutate,
    updateAffiliateAsync: mutateAsync,
    isLoading: isPending,
    isError,
    isSuccess,
    error,
  };
};
